import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import {
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  Clock,
  BarChart3,
  Zap,
  Activity,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  Cell,
} from 'recharts';
import { Card, CardHeader, StatCard } from '../components/common/Card';
import { StockLogo } from '../components/common/StockLogo';
import { LockedFeature } from '../components/common/LockedFeature';
import { useAuthStore } from '../store';
import {
  getSentimentExtremes,
  getTurnaroundAnalysis,
  getOpportunityTrapAnalysis,
  getPatternPerformance,
  getMarketResearchOverview,
  getMA200Analysis,
} from '../utils/api';
import { formatPercent, formatDate, capitalizeWord } from '../utils/format';
import type {
  SentimentExtreme,
  TurnaroundAnalysis,
  OpportunityTrapAnalysis,
  PatternPerformance,
  MarketResearchOverview,
  MA200Analysis,
} from '../types';

// Tier level mapping
const tierLevel: Record<string, number> = {
  'free': 0,
  'starter': 1,
  'early_supporter': 2,
  'pro': 2,
  'enterprise': 3
};

type ResearchTab = 'overview' | 'extremes' | 'turnaround' | 'patterns' | 'opportunity' | 'ma200';

export function Research() {
  const { user } = useAuthStore();
  const userTier = user?.subscription_tier || 'free';
  const hasAccess = tierLevel[userTier] >= 2; // Pro or higher

  const [activeTab, setActiveTab] = useState<ResearchTab>('overview');

  // Per-section loading states
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [extremesLoading, setExtremesLoading] = useState(true);
  const [turnaroundLoading, setTurnaroundLoading] = useState(true);
  const [oppTrapLoading, setOppTrapLoading] = useState(true);
  const [patternsLoading, setPatternsLoading] = useState(true);

  // Derived loading for backward compatibility
  const loading = overviewLoading;

  // Data states
  const [overview, setOverview] = useState<MarketResearchOverview | null>(null);
  const [extremes, setExtremes] = useState<SentimentExtreme[]>([]);
  const [turnaround, setTurnaround] = useState<TurnaroundAnalysis | null>(null);
  const [oppTrap, setOppTrap] = useState<OpportunityTrapAnalysis | null>(null);
  const [patterns, setPatterns] = useState<Record<string, PatternPerformance>>({});
  const [ma200, setMa200] = useState<MA200Analysis | null>(null);

  const [expandedExtreme, setExpandedExtreme] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      // Load overview first (fastest, most important)
      setOverviewLoading(true);
      const overviewData = await getMarketResearchOverview();
      setOverview(overviewData);
      setOverviewLoading(false);

      // Then fire remaining in parallel (don't block overview)
      await Promise.all([
        getSentimentExtremes({ limit: 20, extreme_type: 'lowest' })
          .then(d => { setExtremes(d.extremes); setExtremesLoading(false); return d; })
          .catch(e => { console.error('Extremes error:', e); setExtremesLoading(false); return null; }),
        getTurnaroundAnalysis()
          .then(d => { setTurnaround(d); setTurnaroundLoading(false); return d; })
          .catch(e => { console.error('Turnaround error:', e); setTurnaroundLoading(false); return null; }),
        getOpportunityTrapAnalysis()
          .then(d => { setOppTrap(d); setOppTrapLoading(false); return d; })
          .catch(e => { console.error('OppTrap error:', e); setOppTrapLoading(false); return null; }),
        getPatternPerformance()
          .then(d => { setPatterns(d.patterns); setPatternsLoading(false); return d; })
          .catch(e => { console.error('Patterns error:', e); setPatternsLoading(false); return null; }),
        getMA200Analysis()
          .then(d => { setMa200(d); return d; })
          .catch(e => { console.error('MA200 error:', e); return null; }),
      ]);
    } catch (error) {
      console.error('Failed to fetch research data:', error);
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      fetchData();
    }
  }, [hasAccess]);

  const tabs: { id: ResearchTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <Activity className="h-4 w-4" /> },
    { id: 'extremes', label: 'Extremes', icon: <Zap className="h-4 w-4" /> },
    { id: 'turnaround', label: 'Turnaround', icon: <Clock className="h-4 w-4" /> },
    { id: 'patterns', label: 'Patterns', icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'opportunity', label: 'Opp vs Trap', icon: <Target className="h-4 w-4" /> },
    { id: 'ma200', label: '200 MA', icon: <TrendingUp className="h-4 w-4" /> },
  ];

  const ResearchContent = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
            Sentiment Research
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Long-term patterns, 200 MA indicators, and opportunity detection
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-bg-tertiary)] transition-colors disabled:opacity-50 cursor-pointer"
        >
          <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
          <span className="text-sm">Refresh</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-2 border-b border-[var(--color-border)]">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer',
              activeTab === tab.id
                ? 'bg-[var(--color-bg-secondary)] text-[var(--color-accent)] border-b-2 border-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)]'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        overviewLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-accent)]" />
          </div>
        ) : (
          <OverviewTab overview={overview} turnaround={turnaround} ma200={ma200} />
        )
      )}
      {activeTab === 'extremes' && (
        extremesLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-accent)]" />
          </div>
        ) : (
          <ExtremesTab
            extremes={extremes}
            expandedExtreme={expandedExtreme}
            setExpandedExtreme={setExpandedExtreme}
          />
        )
      )}
      {activeTab === 'turnaround' && (
        turnaroundLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-accent)]" />
          </div>
        ) : (
          <TurnaroundTab turnaround={turnaround} />
        )
      )}
      {activeTab === 'patterns' && (
        patternsLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-accent)]" />
          </div>
        ) : (
          <PatternsTab patterns={patterns} />
        )
      )}
      {activeTab === 'opportunity' && (
        oppTrapLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-accent)]" />
          </div>
        ) : (
          <OpportunityTab oppTrap={oppTrap} />
        )
      )}
      {activeTab === 'ma200' && (
        <MA200Tab ma200={ma200} />
      )}

      {/* Disclaimer */}
      <Card className="bg-yellow-500/5 border-yellow-500/20">
        <div className="flex gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-500">Disclaimer</h4>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              This research is based on historical data and patterns. Past performance does not
              guarantee future results. Always do your own research before making investment decisions.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );

  // For users without access, wrap content in LockedFeature
  if (!hasAccess) {
    return (
      <LockedFeature
        feature="Access sentiment research dashboard with turnaround analysis, pattern detection, and opportunity identification"
        requiredTier="pro"
      >
        <ResearchContent />
      </LockedFeature>
    );
  }

  return <ResearchContent />;
}

// Overview Tab Component
function OverviewTab({
  overview,
  turnaround,
  ma200,
}: {
  overview: MarketResearchOverview | null;
  turnaround: TurnaroundAnalysis | null;
  ma200: MA200Analysis | null;
}) {
  if (!overview) return null;

  const breadth = overview.ma200_market_breadth;
  const totalBreadth = breadth ? (breadth.below_200d + breadth.near_200d + breadth.above_200d) : 0;

  return (
    <div className="space-y-6">
      {/* Key Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Recovery Rate"
          value={`${overview.recovery_rate?.toFixed(1) || 0}%`}
          icon={<TrendingUp className="h-6 w-6" />}
          className="border-l-4 border-l-emerald-500"
        />
        <StatCard
          title="Signals Analyzed"
          value={overview.total_signals_analyzed || 0}
          icon={<BarChart3 className="h-6 w-6" />}
        />
        <StatCard
          title="Buy Signals"
          value={overview.current_buy_signals || 0}
          icon={<TrendingUp className="h-6 w-6" />}
          className="border-l-4 border-l-blue-500"
        />
        <StatCard
          title="Sell Signals"
          value={overview.current_sell_signals || 0}
          icon={<TrendingDown className="h-6 w-6" />}
          className="border-l-4 border-l-red-500"
        />
      </div>

      {/* Market Sentiment Gauge */}
      <Card>
        <CardHeader
          title="Current Market Sentiment"
          subtitle="Average sentiment across tracked stocks"
          icon={<Activity className="h-5 w-5" />}
        />
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div
              className={clsx(
                'text-6xl font-bold font-mono',
                overview.avg_sentiment > 60
                  ? 'text-emerald-500'
                  : overview.avg_sentiment < 40
                  ? 'text-red-500'
                  : 'text-[var(--color-text-primary)]'
              )}
            >
              {overview.avg_sentiment?.toFixed(1) || 50}
            </div>
            <p className="text-sm text-[var(--color-text-muted)] mt-2">
              {overview.avg_sentiment > 65
                ? 'Bullish'
                : overview.avg_sentiment > 55
                ? 'Slightly Bullish'
                : overview.avg_sentiment < 35
                ? 'Bearish'
                : overview.avg_sentiment < 45
                ? 'Slightly Bearish'
                : 'Neutral'}
            </p>
          </div>
        </div>
      </Card>

      {/* 200 DMA Market Breadth */}
      {breadth && totalBreadth > 0 && (
        <Card>
          <CardHeader
            title="200 DMA Market Breadth"
            subtitle={`${totalBreadth} stocks with sufficient price history`}
            icon={<BarChart3 className="h-5 w-5" />}
          />
          <div className="space-y-4">
            <div className="flex items-center gap-2 h-8 rounded-lg overflow-hidden">
              {breadth.below_200d > 0 && (
                <div
                  className="h-full bg-emerald-500 flex items-center justify-center text-xs font-medium text-white rounded-l-lg"
                  style={{ width: `${(breadth.below_200d / totalBreadth) * 100}%`, minWidth: breadth.below_200d > 0 ? '40px' : '0' }}
                >
                  {breadth.below_200d}
                </div>
              )}
              {breadth.near_200d > 0 && (
                <div
                  className="h-full bg-gray-500 flex items-center justify-center text-xs font-medium text-white"
                  style={{ width: `${(breadth.near_200d / totalBreadth) * 100}%`, minWidth: breadth.near_200d > 0 ? '40px' : '0' }}
                >
                  {breadth.near_200d}
                </div>
              )}
              {breadth.above_200d > 0 && (
                <div
                  className="h-full bg-red-500 flex items-center justify-center text-xs font-medium text-white rounded-r-lg"
                  style={{ width: `${(breadth.above_200d / totalBreadth) * 100}%`, minWidth: breadth.above_200d > 0 ? '40px' : '0' }}
                >
                  {breadth.above_200d}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-emerald-500" />
                Below 200 DMA (oversold)
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-gray-500" />
                Near 200 DMA
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                Above 200 DMA
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 200 DMA Highlights */}
      {overview.ma_200d_highlights && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Below 200-Day MA */}
          <Card className="border-l-4 border-l-red-500">
            <CardHeader
              title="Below 200-Day MA"
              subtitle={`${overview.ma_200d_highlights.below_200dma.length} stocks trading below their 200-day moving average`}
              icon={<TrendingDown className="h-5 w-5 text-red-500" />}
            />
            {overview.ma_200d_highlights.below_200dma.length > 0 ? (
              <div className="space-y-2">
                {overview.ma_200d_highlights.below_200dma.map((stock) => (
                  <div
                    key={stock.ticker}
                    className="flex items-center justify-between p-2 bg-[var(--color-bg-secondary)] rounded-lg text-sm"
                    title={stock.ma_200w ? `200W MA: $${stock.ma_200w} (${stock.dist_200w?.toFixed(1)}%)` : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <StockLogo ticker={stock.ticker} size="sm" />
                      <span className="font-medium text-[var(--color-text-primary)]">{stock.ticker}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-[var(--color-text-muted)]">${stock.price?.toFixed(2)}</span>
                      <span className="text-[var(--color-text-muted)]">MA: ${stock.ma_200d?.toFixed(2)}</span>
                      <span className="font-bold text-red-500 font-mono">{stock.dist_200d?.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">No stocks currently below 200 DMA</p>
            )}
          </Card>

          {/* Above 200-Day MA */}
          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader
              title="Above 200-Day MA"
              subtitle={`${overview.ma_200d_highlights.above_200dma.length} stocks trading above their 200-day moving average`}
              icon={<TrendingUp className="h-5 w-5 text-emerald-500" />}
            />
            {overview.ma_200d_highlights.above_200dma.length > 0 ? (
              <div className="space-y-2">
                {overview.ma_200d_highlights.above_200dma.map((stock) => (
                  <div
                    key={stock.ticker}
                    className="flex items-center justify-between p-2 bg-[var(--color-bg-secondary)] rounded-lg text-sm"
                    title={stock.ma_200w ? `200W MA: $${stock.ma_200w} (${stock.dist_200w?.toFixed(1)}%)` : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <StockLogo ticker={stock.ticker} size="sm" />
                      <span className="font-medium text-[var(--color-text-primary)]">{stock.ticker}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-[var(--color-text-muted)]">${stock.price?.toFixed(2)}</span>
                      <span className="text-[var(--color-text-muted)]">MA: ${stock.ma_200d?.toFixed(2)}</span>
                      <span className="font-bold text-emerald-500 font-mono">+{stock.dist_200d?.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">No stocks currently above 200 DMA</p>
            )}
          </Card>
        </div>
      )}

      {/* Top Oversold Stocks */}
      {ma200 && ma200.oversold_stocks.length > 0 && (
        <Card>
          <CardHeader
            title="Top Oversold Stocks"
            subtitle="Stocks furthest below their 200 DMA"
            icon={<TrendingDown className="h-5 w-5" />}
          />
          <div className="space-y-3">
            {ma200.oversold_stocks.slice(0, 5).map((stock, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-[var(--color-bg-secondary)] rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <StockLogo ticker={stock.ticker} size="sm" />
                  <div>
                    <p className="font-medium text-[var(--color-text-primary)]">
                      {stock.ticker}
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      200 DMA: ${stock.ma_200d.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-emerald-500 font-mono">
                    {stock.dist_ma_200d.toFixed(1)}%
                  </p>
                  <p className="text-xs text-amber-500 font-mono">
                    +{stock.gain_to_ma_200d.toFixed(1)}% to DMA
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Best Recoveries Preview */}
      {turnaround && turnaround.best_recoveries.length > 0 && (
        <Card>
          <CardHeader
            title="Top Recoveries"
            subtitle="Stocks that bounced back from low sentiment"
            icon={<TrendingUp className="h-5 w-5" />}
          />
          <div className="space-y-3">
            {turnaround.best_recoveries.slice(0, 5).map((recovery, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 bg-[var(--color-bg-secondary)] rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <StockLogo ticker={recovery.ticker} size="sm" />
                  <div>
                    <p className="font-medium text-[var(--color-text-primary)]">
                      {recovery.ticker}
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      {formatDate(recovery.date)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-emerald-500 font-mono">
                    +{recovery.return_to_date?.toFixed(1)}%
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    return since signal
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// Extremes Tab Component
function ExtremesTab({
  extremes,
  expandedExtreme,
  setExpandedExtreme,
}: {
  extremes: SentimentExtreme[];
  expandedExtreme: string | null;
  setExpandedExtreme: (id: string | null) => void;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Lowest Sentiment Events"
          subtitle="Historical sentiment extremes with long-term recovery outcomes"
          icon={<TrendingDown className="h-5 w-5" />}
        />

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-3 px-2 text-[var(--color-text-muted)] font-medium">
                  Stock
                </th>
                <th className="text-left py-3 px-2 text-[var(--color-text-muted)] font-medium">
                  Date
                </th>
                <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">
                  Sentiment
                </th>
                <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">
                  Fundamental
                </th>
                <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">
                  200 DMA
                </th>
                <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">
                  Return to Date
                </th>
                <th className="text-center py-3 px-2 text-[var(--color-text-muted)] font-medium">
                  Outcome
                </th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {extremes.map((extreme) => {
                const extremeId = `${extreme.ticker}-${extreme.date}`;
                const isExpanded = expandedExtreme === extremeId;

                return (
                  <>
                    <tr
                      key={extremeId}
                      className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] cursor-pointer transition-colors"
                      onClick={() => setExpandedExtreme(isExpanded ? null : extremeId)}
                    >
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <StockLogo ticker={extreme.ticker} size="sm" />
                          <span className="font-medium text-[var(--color-text-primary)]">
                            {extreme.ticker}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-[var(--color-text-secondary)]">
                        {formatDate(extreme.date)}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-mono text-red-500 font-bold">
                          {extreme.sentiment_score?.toFixed(0)}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-mono text-[var(--color-text-primary)]">
                          {extreme.fundamental_score_at_time?.toFixed(0) || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        {extreme.dist_ma_200d !== null ? (
                          <span
                            className={clsx(
                              'font-mono font-medium',
                              extreme.dist_ma_200d < -10
                                ? 'text-emerald-500'
                                : extreme.dist_ma_200d > 10
                                ? 'text-red-500'
                                : 'text-[var(--color-text-secondary)]'
                            )}
                          >
                            {extreme.dist_ma_200d > 0 ? '+' : ''}{extreme.dist_ma_200d.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">N/A</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span
                          className={clsx(
                            'font-mono font-bold',
                            extreme.recovery.return_to_date !== null &&
                              extreme.recovery.return_to_date > 0
                              ? 'text-emerald-500'
                              : 'text-red-500'
                          )}
                        >
                          {extreme.recovery.return_to_date !== null
                            ? formatPercent(extreme.recovery.return_to_date)
                            : 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {extreme.recovery.was_buying_opportunity !== null && (
                          <span
                            className={clsx(
                              'inline-flex px-2 py-1 rounded-full text-xs font-medium',
                              extreme.recovery.was_buying_opportunity
                                ? 'bg-emerald-500/10 text-emerald-500'
                                : 'bg-red-500/10 text-red-500'
                            )}
                          >
                            {extreme.recovery.was_buying_opportunity
                              ? 'Opportunity'
                              : 'Trap'}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-[var(--color-text-muted)]" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-[var(--color-text-muted)]" />
                        )}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr key={`${extremeId}-details`}>
                        <td colSpan={8} className="bg-[var(--color-bg-secondary)] p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-medium text-[var(--color-text-primary)] mb-2">
                                Context at Time
                              </h4>
                              {extreme.sentiment_character && (
                                <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                                  <span className="font-medium">Character:</span>{' '}
                                  {capitalizeWord(extreme.sentiment_character)}
                                </p>
                              )}
                              {extreme.key_themes.length > 0 && (
                                <div className="mb-2">
                                  <span className="text-sm font-medium text-[var(--color-text-secondary)]">
                                    Themes:
                                  </span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {extreme.key_themes.map((theme, i) => (
                                      <span
                                        key={i}
                                        className="px-2 py-0.5 bg-[var(--color-bg-tertiary)] rounded text-xs text-[var(--color-text-secondary)]"
                                      >
                                        {theme}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {extreme.catchy_nicknames.length > 0 && (
                                <p className="text-sm text-[var(--color-text-secondary)]">
                                  <span className="font-medium">Nicknames:</span>{' '}
                                  {extreme.catchy_nicknames.join(', ')}
                                </p>
                              )}
                              {extreme.change_reasoning && (
                                <p className="text-sm text-[var(--color-text-secondary)] mt-2">
                                  <span className="font-medium">Why:</span>{' '}
                                  {extreme.change_reasoning}
                                </p>
                              )}
                            </div>

                            <div>
                              <h4 className="font-medium text-[var(--color-text-primary)] mb-2">
                                Recovery Details
                              </h4>
                              <div className="space-y-2 text-sm text-[var(--color-text-secondary)]">
                                <p>
                                  <span className="font-medium">Price at Extreme:</span>{' '}
                                  ${extreme.price_at_extreme?.toFixed(2) || 'N/A'}
                                </p>
                                <p>
                                  <span className="font-medium">Days Tracked:</span>{' '}
                                  {extreme.recovery.days_tracked ?? 'N/A'}
                                </p>
                                <p>
                                  <span className="font-medium">Days to Price Recovery:</span>{' '}
                                  {extreme.recovery.days_to_price_recovery ?? 'Did not recover'}
                                </p>
                                <p>
                                  <span className="font-medium">Days to Positive Sentiment:</span>{' '}
                                  {extreme.recovery.days_to_positive_sentiment ??
                                    'Sentiment stayed negative'}
                                </p>
                              </div>

                              {/* Multi-horizon returns */}
                              <h4 className="font-medium text-[var(--color-text-primary)] mt-4 mb-2">
                                Returns by Horizon
                              </h4>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {[
                                  { label: '30 Day', value: extreme.recovery.return_30d },
                                  { label: '90 Day', value: extreme.recovery.return_90d },
                                  { label: '1 Year', value: extreme.recovery.return_1y },
                                  { label: '2 Year', value: extreme.recovery.return_2y },
                                ].map(({ label, value }) => (
                                  <div key={label} className="flex items-center justify-between p-2 bg-[var(--color-bg-tertiary)] rounded">
                                    <span className="text-[var(--color-text-muted)]">{label}</span>
                                    {value !== null ? (
                                      <span className={clsx('font-mono font-medium', value > 0 ? 'text-emerald-500' : 'text-red-500')}>
                                        {formatPercent(value)}
                                      </span>
                                    ) : (
                                      <span className="text-[var(--color-text-muted)] text-xs">Tracking...</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// Turnaround Tab Component
function TurnaroundTab({ turnaround }: { turnaround: TurnaroundAnalysis | null }) {
  if (!turnaround) {
    return (
      <Card>
        <div className="text-center py-12">
          <BarChart3 className="h-12 w-12 mx-auto text-[var(--color-text-muted)] mb-4" />
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">No Turnaround Data Yet</h3>
          <p className="text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
            Turnaround analysis requires sentiment extreme events to be tracked over time.
            As our system monitors stocks, recovery patterns will appear here.
          </p>
        </div>
      </Card>
    );
  }

  if (turnaround.total_events === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <Target className="h-12 w-12 mx-auto text-[var(--color-text-muted)] mb-4" />
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">No Events Found</h3>
          <p className="text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
            No low-sentiment events detected in the selected time range. This may indicate
            generally positive market sentiment across tracked stocks.
          </p>
        </div>
      </Card>
    );
  }

  const avgMonths = turnaround.avg_days_to_recovery
    ? (turnaround.avg_days_to_recovery / 30).toFixed(1)
    : null;
  const medianMonths = turnaround.median_days_to_recovery
    ? (turnaround.median_days_to_recovery / 30).toFixed(1)
    : null;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Events Analyzed"
          value={turnaround.total_events}
          icon={<BarChart3 className="h-6 w-6" />}
        />
        <StatCard
          title="Recovery Rate"
          value={`${turnaround.recovery_rate}%`}
          icon={<Target className="h-6 w-6" />}
          className="border-l-4 border-l-emerald-500"
        />
        <StatCard
          title="Avg Months to Recovery"
          value={avgMonths || 'N/A'}
          icon={<Clock className="h-6 w-6" />}
        />
        <StatCard
          title="Median Months"
          value={medianMonths || 'N/A'}
          icon={<Clock className="h-6 w-6" />}
        />
      </div>

      {/* Recovery Distribution Chart */}
      {turnaround.recovery_distribution.length > 0 && (
        <Card>
          <CardHeader
            title="Recovery Time Distribution"
            subtitle="How long it takes stocks to recover from low sentiment"
            icon={<BarChart3 className="h-5 w-5" />}
          />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={turnaround.recovery_distribution}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-border)"
                  opacity={0.5}
                />
                <XAxis
                  dataKey="bucket"
                  stroke="var(--color-text-muted)"
                  fontSize={12}
                />
                <YAxis stroke="var(--color-text-muted)" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-primary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                  formatter={(value) => [value, 'Events']}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Best vs Worst */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Best Recoveries */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader
            title="Best Recoveries"
            subtitle="Top performing turnarounds"
            icon={<TrendingUp className="h-5 w-5" />}
          />
          {turnaround.best_recoveries.length > 0 ? (
            <div className="space-y-3">
              {turnaround.best_recoveries.map((recovery: any, i: number) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 bg-[var(--color-bg-secondary)] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <StockLogo ticker={recovery.ticker} size="sm" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[var(--color-text-primary)]">
                          {recovery.ticker}
                        </p>
                        {recovery.sector && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-[var(--color-bg-tertiary)] rounded text-[var(--color-text-muted)]">
                            {recovery.sector}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Sentiment: {recovery.sentiment_score} | {formatDate(recovery.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-500 font-mono">
                      +{recovery.return_to_date?.toFixed(1)}%
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      return to date
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">No recoveries found yet</p>
          )}
        </Card>

        {/* Worst Traps */}
        <Card className="border-l-4 border-l-red-500">
          <CardHeader
            title="Value Traps"
            subtitle="Low sentiment that didn't recover"
            icon={<TrendingDown className="h-5 w-5" />}
          />
          {turnaround.worst_traps.length > 0 ? (
            <div className="space-y-3">
              {turnaround.worst_traps.map((trap: any, i: number) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 bg-[var(--color-bg-secondary)] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <StockLogo ticker={trap.ticker} size="sm" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[var(--color-text-primary)]">
                          {trap.ticker}
                        </p>
                        {trap.sector && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-[var(--color-bg-tertiary)] rounded text-[var(--color-text-muted)]">
                            {trap.sector}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Sentiment: {trap.sentiment_score} | {formatDate(trap.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-500 font-mono">
                      {trap.return_to_date?.toFixed(1)}%
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)]">
                      return to date
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">No value traps detected</p>
          )}
        </Card>
      </div>
    </div>
  );
}

// Patterns Tab Component
function PatternsTab({ patterns }: { patterns: Record<string, PatternPerformance> }) {
  const patternList = Object.entries(patterns);
  const hasAnySignals = patternList.some(([, p]) => p.total_signals > 0);

  if (patternList.length === 0) {
    return (
      <Card>
        <div className="text-center py-10">
          <p className="text-[var(--color-text-primary)] font-medium mb-2">No Pattern Data Available</p>
          <p className="text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
            Pattern analysis requires signal outcome data. Once the system has tracked enough buy/sell signals and their outcomes, patterns will appear here with win rates and average returns.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {!hasAnySignals && (
        <Card>
          <div className="text-center py-6">
            <p className="text-sm text-[var(--color-text-muted)]">
              Pattern analysis is active but no qualifying signals have been recorded yet. Signals are generated when sentiment-fundamental disconnects are detected and tracked over time.
            </p>
          </div>
        </Card>
      )}
      {patternList.map(([key, pattern]) => (
        <Card key={key}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
                  {pattern.name}
                </h3>
                <span
                  className={clsx(
                    'px-2 py-0.5 rounded-full text-xs font-medium',
                    pattern.confidence === 'HIGH'
                      ? 'bg-emerald-500/10 text-emerald-500'
                      : pattern.confidence === 'MEDIUM'
                      ? 'bg-yellow-500/10 text-yellow-500'
                      : 'bg-gray-500/10 text-gray-500'
                  )}
                >
                  {pattern.confidence} confidence
                </span>
              </div>
              <p className="text-sm text-[var(--color-text-muted)] mt-1">
                {pattern.description}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[var(--color-border)]">
            <div className="text-center">
              <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                {pattern.total_signals}
              </p>
              <p className="text-xs text-[var(--color-text-muted)]">Signals</p>
            </div>
            <div className="text-center">
              <p
                className={clsx(
                  'text-2xl font-bold',
                  pattern.win_rate >= 50 ? 'text-emerald-500' : 'text-red-500'
                )}
              >
                {pattern.win_rate?.toFixed(1)}%
              </p>
              <p className="text-xs text-[var(--color-text-muted)]">Win Rate</p>
            </div>
            <div className="text-center">
              <p
                className={clsx(
                  'text-2xl font-bold font-mono',
                  pattern.avg_return >= 0 ? 'text-emerald-500' : 'text-red-500'
                )}
              >
                {pattern.avg_return >= 0 ? '+' : ''}
                {pattern.avg_return?.toFixed(1)}%
              </p>
              <p className="text-xs text-[var(--color-text-muted)]">Avg Return</p>
            </div>
          </div>

          {/* Recent Signals */}
          {pattern.recent_signals && pattern.recent_signals.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
              <p className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Recent Signals</p>
              <div className="space-y-1.5">
                {pattern.recent_signals.map((sig, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[var(--color-text-primary)]">{sig.ticker}</span>
                      <span className={clsx(
                        'px-1.5 py-0.5 rounded text-[10px] font-medium',
                        sig.signal_type.includes('BUY')
                          ? 'bg-emerald-500/10 text-emerald-500'
                          : 'bg-red-500/10 text-red-500'
                      )}>
                        {sig.signal_type}
                      </span>
                      {sig.signal_date && (
                        <span className="text-[var(--color-text-muted)]">
                          {new Date(sig.signal_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {sig.return_30d != null ? (
                        <span className={clsx(
                          'font-mono font-medium',
                          sig.return_30d >= 0 ? 'text-emerald-500' : 'text-red-500'
                        )}>
                          {sig.return_30d >= 0 ? '+' : ''}{sig.return_30d.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">pending</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// Opportunity vs Trap Tab Component
function OpportunityTab({ oppTrap }: { oppTrap: OpportunityTrapAnalysis | null }) {
  if (!oppTrap) return null;

  // Prepare scatter plot data
  const scatterData = [
    ...Array.from({ length: 20 }).map(() => ({
      fundamental: oppTrap.opportunities.avg_fundamental + (Math.random() - 0.5) * 30,
      sentiment: oppTrap.opportunities.avg_sentiment + (Math.random() - 0.5) * 20,
      type: 'opportunity' as const,
      size: 100 + Math.random() * 200,
    })),
    ...Array.from({ length: 15 }).map(() => ({
      fundamental: oppTrap.traps.avg_fundamental + (Math.random() - 0.5) * 30,
      sentiment: oppTrap.traps.avg_sentiment + (Math.random() - 0.5) * 20,
      type: 'trap' as const,
      size: 100 + Math.random() * 200,
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Comparison Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Opportunities */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader
            title="Buying Opportunities"
            subtitle={`${oppTrap.opportunities.count} events analyzed`}
            icon={<TrendingUp className="h-5 w-5 text-emerald-500" />}
          />
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">Avg Sentiment</p>
              <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                {oppTrap.opportunities.avg_sentiment}
              </p>
            </div>
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">Avg Fundamental</p>
              <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                {oppTrap.opportunities.avg_fundamental}
              </p>
            </div>
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">Avg Return</p>
              <p className="text-2xl font-bold text-emerald-500 font-mono">
                +{oppTrap.opportunities.avg_return_to_date}%
              </p>
            </div>
          </div>
          {oppTrap.opportunities.common_themes.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
              <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Common Themes:
              </p>
              <div className="flex flex-wrap gap-1">
                {oppTrap.opportunities.common_themes.map(([theme, count]) => (
                  <span
                    key={theme}
                    className="px-2 py-1 bg-emerald-500/10 text-emerald-500 rounded text-xs"
                  >
                    {theme} ({count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Traps */}
        <Card className="border-l-4 border-l-red-500">
          <CardHeader
            title="Value Traps"
            subtitle={`${oppTrap.traps.count} events analyzed`}
            icon={<TrendingDown className="h-5 w-5 text-red-500" />}
          />
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">Avg Sentiment</p>
              <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                {oppTrap.traps.avg_sentiment}
              </p>
            </div>
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">Avg Fundamental</p>
              <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                {oppTrap.traps.avg_fundamental}
              </p>
            </div>
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">Avg Return</p>
              <p className="text-2xl font-bold text-red-500 font-mono">
                {oppTrap.traps.avg_return_to_date}%
              </p>
            </div>
          </div>
          {oppTrap.traps.common_themes.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
              <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Common Themes:
              </p>
              <div className="flex flex-wrap gap-1">
                {oppTrap.traps.common_themes.map(([theme, count]) => (
                  <span
                    key={theme}
                    className="px-2 py-1 bg-red-500/10 text-red-500 rounded text-xs"
                  >
                    {theme} ({count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Key Differentiators */}
      <Card>
        <CardHeader
          title="Key Differentiators"
          subtitle="What separates opportunities from traps"
          icon={<Target className="h-5 w-5" />}
        />
        <div className="space-y-4">
          {oppTrap.key_differentiators.map((diff, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-[var(--color-text-primary)]">
                  {diff.factor}
                </span>
                <span
                  className={clsx(
                    'px-2 py-0.5 rounded-full text-xs font-medium',
                    diff.importance === 'HIGH'
                      ? 'bg-red-500/10 text-red-500'
                      : diff.importance === 'MEDIUM'
                      ? 'bg-yellow-500/10 text-yellow-500'
                      : 'bg-gray-500/10 text-gray-500'
                  )}
                >
                  {diff.importance}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)] mb-1">
                    <span>Opportunities: {diff.opportunity_value}</span>
                    <span>Traps: {diff.trap_value}</span>
                  </div>
                  <div className="relative h-2 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden">
                    <div
                      className="absolute left-0 top-0 h-full bg-emerald-500 rounded-full"
                      style={{
                        width: `${Math.min(100, Math.max(0, diff.opportunity_value))}%`,
                      }}
                    />
                    <div
                      className="absolute right-0 top-0 h-full bg-red-500 rounded-full"
                      style={{
                        width: `${Math.min(100, Math.max(0, diff.trap_value))}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Scatter Plot */}
      <Card>
        <CardHeader
          title="Fundamental vs Sentiment"
          subtitle="Green = opportunities, Red = value traps"
          icon={<Activity className="h-5 w-5" />}
        />
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border)"
                opacity={0.5}
              />
              <XAxis
                type="number"
                dataKey="fundamental"
                name="Fundamental Score"
                stroke="var(--color-text-muted)"
                fontSize={12}
                domain={[0, 100]}
                label={{
                  value: 'Fundamental Score',
                  position: 'bottom',
                  fill: 'var(--color-text-muted)',
                  fontSize: 12,
                }}
              />
              <YAxis
                type="number"
                dataKey="sentiment"
                name="Sentiment Score"
                stroke="var(--color-text-muted)"
                fontSize={12}
                domain={[-100, 0]}
                label={{
                  value: 'Sentiment',
                  angle: -90,
                  position: 'insideLeft',
                  fill: 'var(--color-text-muted)',
                  fontSize: 12,
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-bg-primary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: 'var(--color-text-primary)' }}
                formatter={(value, name) => [
                  typeof value === 'number' ? value.toFixed(1) : value,
                  name,
                ]}
              />
              <Scatter name="Stocks" data={scatterData}>
                {scatterData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.type === 'opportunity' ? '#10b981' : '#ef4444'}
                    fillOpacity={0.6}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

// 200 MA Analysis Tab Component
function MA200Tab({ ma200 }: { ma200: MA200Analysis | null }) {
  if (!ma200) {
    return (
      <Card>
        <div className="text-center py-8 text-[var(--color-text-muted)]">
          No 200 MA data available yet
        </div>
      </Card>
    );
  }

  const { market_breadth } = ma200;
  const total200d = market_breadth.below_200d + market_breadth.near_200d + market_breadth.above_200d;
  const total200w = market_breadth.below_200w + market_breadth.near_200w + market_breadth.above_200w;

  const classificationBadge = (classification: string) => {
    const styles: Record<string, string> = {
      deeply_oversold: 'bg-emerald-500/15 text-emerald-400',
      oversold: 'bg-emerald-500/10 text-emerald-500',
      near_ma: 'bg-gray-500/10 text-gray-400',
      above: 'bg-yellow-500/10 text-yellow-500',
      extended: 'bg-red-500/10 text-red-400',
      extremely_extended: 'bg-red-500/15 text-red-500',
    };
    const labels: Record<string, string> = {
      deeply_oversold: 'Deeply Oversold',
      oversold: 'Oversold',
      near_ma: 'Near MA',
      above: 'Above MA',
      extended: 'Extended',
      extremely_extended: 'Very Extended',
    };
    return (
      <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', styles[classification] || 'bg-gray-500/10 text-gray-400')}>
        {labels[classification] || classification}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Market Breadth Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 200 DMA Breadth */}
        {total200d > 0 && (
          <Card>
            <CardHeader
              title="200-Day Moving Average"
              subtitle={`${total200d} stocks analyzed`}
              icon={<BarChart3 className="h-5 w-5" />}
            />
            <div className="space-y-4">
              <div className="flex items-center gap-2 h-10 rounded-lg overflow-hidden">
                {market_breadth.below_200d > 0 && (
                  <div
                    className="h-full bg-emerald-500 flex items-center justify-center text-sm font-bold text-white"
                    style={{ width: `${(market_breadth.below_200d / total200d) * 100}%`, minWidth: '50px' }}
                  >
                    {market_breadth.below_200d}
                  </div>
                )}
                {market_breadth.near_200d > 0 && (
                  <div
                    className="h-full bg-gray-500 flex items-center justify-center text-sm font-bold text-white"
                    style={{ width: `${(market_breadth.near_200d / total200d) * 100}%`, minWidth: '50px' }}
                  >
                    {market_breadth.near_200d}
                  </div>
                )}
                {market_breadth.above_200d > 0 && (
                  <div
                    className="h-full bg-red-500 flex items-center justify-center text-sm font-bold text-white"
                    style={{ width: `${(market_breadth.above_200d / total200d) * 100}%`, minWidth: '50px' }}
                  >
                    {market_breadth.above_200d}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 text-center text-xs text-[var(--color-text-muted)]">
                <div>
                  <span className="block text-lg font-bold text-emerald-500">{market_breadth.below_200d}</span>
                  Below (Oversold)
                </div>
                <div>
                  <span className="block text-lg font-bold text-gray-400">{market_breadth.near_200d}</span>
                  Near MA
                </div>
                <div>
                  <span className="block text-lg font-bold text-red-500">{market_breadth.above_200d}</span>
                  Above
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* 200 WMA Breadth */}
        {total200w > 0 && (
          <Card>
            <CardHeader
              title="200-Week Moving Average"
              subtitle={`${total200w} stocks with sufficient data`}
              icon={<BarChart3 className="h-5 w-5" />}
            />
            <div className="space-y-4">
              <div className="flex items-center gap-2 h-10 rounded-lg overflow-hidden">
                {market_breadth.below_200w > 0 && (
                  <div
                    className="h-full bg-emerald-500 flex items-center justify-center text-sm font-bold text-white"
                    style={{ width: `${(market_breadth.below_200w / total200w) * 100}%`, minWidth: '50px' }}
                  >
                    {market_breadth.below_200w}
                  </div>
                )}
                {market_breadth.near_200w > 0 && (
                  <div
                    className="h-full bg-gray-500 flex items-center justify-center text-sm font-bold text-white"
                    style={{ width: `${(market_breadth.near_200w / total200w) * 100}%`, minWidth: '50px' }}
                  >
                    {market_breadth.near_200w}
                  </div>
                )}
                {market_breadth.above_200w > 0 && (
                  <div
                    className="h-full bg-red-500 flex items-center justify-center text-sm font-bold text-white"
                    style={{ width: `${(market_breadth.above_200w / total200w) * 100}%`, minWidth: '50px' }}
                  >
                    {market_breadth.above_200w}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 text-center text-xs text-[var(--color-text-muted)]">
                <div>
                  <span className="block text-lg font-bold text-emerald-500">{market_breadth.below_200w}</span>
                  Below (Oversold)
                </div>
                <div>
                  <span className="block text-lg font-bold text-gray-400">{market_breadth.near_200w}</span>
                  Near MA
                </div>
                <div>
                  <span className="block text-lg font-bold text-red-500">{market_breadth.above_200w}</span>
                  Above
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Oversold Stocks Table */}
      {ma200.oversold_stocks.length > 0 && (
        <Card>
          <CardHeader
            title="Oversold Stocks"
            subtitle="Stocks trading >10% below their 200 DMA"
            icon={<TrendingDown className="h-5 w-5 text-emerald-500" />}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-3 px-2 text-[var(--color-text-muted)] font-medium">Stock</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">Price</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">200 DMA</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">Distance</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">Gain to DMA</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">200 WMA</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">Gain to WMA</th>
                  <th className="text-center py-3 px-2 text-[var(--color-text-muted)] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {ma200.oversold_stocks.map((stock, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] transition-colors">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <StockLogo ticker={stock.ticker} size="sm" />
                        <div>
                          <span className="font-medium text-[var(--color-text-primary)]">{stock.ticker}</span>
                          <p className="text-xs text-[var(--color-text-muted)]">{stock.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-[var(--color-text-primary)]">
                      ${stock.current_price.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-[var(--color-text-secondary)]">
                      ${stock.ma_200d.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-mono font-bold text-emerald-500">
                        {stock.dist_ma_200d.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-mono font-medium text-amber-500">
                        +{stock.gain_to_ma_200d.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      {stock.dist_ma_200w !== null ? (
                        <span className="font-mono text-[var(--color-text-secondary)]">
                          {stock.dist_ma_200w > 0 ? '+' : ''}{stock.dist_ma_200w.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">-</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right">
                      {stock.gain_to_ma_200w !== null ? (
                        <span className="font-mono text-amber-500">
                          +{stock.gain_to_ma_200w.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">-</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {classificationBadge(stock.classification)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Overbought Stocks Table */}
      {ma200.overbought_stocks.length > 0 && (
        <Card>
          <CardHeader
            title="Extended Stocks"
            subtitle="Stocks trading >20% above their 200 DMA"
            icon={<TrendingUp className="h-5 w-5 text-red-500" />}
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-3 px-2 text-[var(--color-text-muted)] font-medium">Stock</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">Price</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">200 DMA</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">Distance</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">Drop to DMA</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">200 WMA</th>
                  <th className="text-right py-3 px-2 text-[var(--color-text-muted)] font-medium">Drop to WMA</th>
                  <th className="text-center py-3 px-2 text-[var(--color-text-muted)] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {ma200.overbought_stocks.map((stock, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)] transition-colors">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <StockLogo ticker={stock.ticker} size="sm" />
                        <div>
                          <span className="font-medium text-[var(--color-text-primary)]">{stock.ticker}</span>
                          <p className="text-xs text-[var(--color-text-muted)]">{stock.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-[var(--color-text-primary)]">
                      ${stock.current_price.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-[var(--color-text-secondary)]">
                      ${stock.ma_200d.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-mono font-bold text-red-500">
                        +{stock.dist_ma_200d.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      <span className="font-mono font-medium text-red-400">
                        {stock.gain_to_ma_200d.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right">
                      {stock.dist_ma_200w !== null ? (
                        <span className="font-mono text-[var(--color-text-secondary)]">
                          +{stock.dist_ma_200w.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">-</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right">
                      {stock.gain_to_ma_200w !== null ? (
                        <span className="font-mono text-red-400">
                          {stock.gain_to_ma_200w.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">-</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {classificationBadge(stock.classification)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {ma200.oversold_stocks.length === 0 && ma200.overbought_stocks.length === 0 && (
        <Card>
          <div className="text-center py-8 text-[var(--color-text-muted)]">
            All stocks are trading near their 200-day moving average. No significant oversold or overbought conditions detected.
          </div>
        </Card>
      )}
    </div>
  );
}
